import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import axios from "axios";
import ProductCard from "../components/Home/ProductCard";
import Loder from "../components/Loder";
import { Range } from "react-range";
import DeliveryInfo from "../components/Home/DeliveryInfo";

const Shop = () => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const location = useLocation();

  // --- State Management ---
  const [values, setValues] = useState([0, 1500]);
  const [appliedValues, setAppliedValues] = useState([0, 1500]);
  const [priceRangeFilter, setPriceRangeFilter] = useState("");
  const [sortOrder, setSortOrder] = useState(""); // NEW: State specifically for sorting
  const [isFilterVisible, setIsFilterVisible] = useState(false); // For mobile collapse
  const [categories, setCategories] = useState([]);
  // --- Category and Sub-category states ---
  const [activeCategory, setActiveCategory] = useState(null);
  const [subCategories, setSubCategories] = useState({});
  const [loadingSubCat, setLoadingSubCat] = useState(null);
  const [isShopOpen, setIsShopOpen] = useState(true);

  // --- Check Shop Status ---
  useQuery({
    queryKey: ["shop-status"],
    queryFn: async () => {
      try {
        const res = await axios.get(`${baseUrl}shop`);
        const status = res.data.shopData?.isOpen ?? true;
        setIsShopOpen(status);
        return res.data;
      } catch (error) {
        console.error("Failed to fetch shop status", error);
        return null;
      }
    },
  });

  useEffect(() => {
    const fetchCategories = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}categories`
      );
      const data = await response.json();
      setCategories(data);
    };
    fetchCategories();
  }, []);

  // --- Dynamic Price Bounds State ---
  const [minBound, setMinBound] = useState(0);
  const [maxBound, setMaxBound] = useState(1500);
  const [hasInitializedBounds, setHasInitializedBounds] = useState(false);
  const STEP = 1;

  // --- Sub-Category Fetching Logic (Unchanged) ---
  const fetchSubCategories = async (categoryId) => {
    if (subCategories[categoryId]) return;
    setLoadingSubCat(categoryId);
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(
        `${baseUrl}sub-category/list?category_id=${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubCategories((prev) => ({
        ...prev,
        [categoryId]: res.data.data || [],
      }));
    } catch (error) {
      console.error("Failed to fetch sub-categories", error);
      toast.error("Failed to fetch sub-categories.");
      setSubCategories((prev) => ({ ...prev, [categoryId]: [] }));
    } finally {
      setLoadingSubCat(null);
    }
  };

  const handleIconToggle = (e, categoryId) => {
    e.stopPropagation();
    const newActiveCategory = activeCategory === categoryId ? null : categoryId;
    setActiveCategory(newActiveCategory);
    if (newActiveCategory) {
      fetchSubCategories(newActiveCategory);
    }
  };

  // --- Filter and Sort Handlers ---
  const handleFilterByPriceRange = () => {
    setAppliedValues([...values]);
    setPriceRangeFilter(`priceRange=${values[0]}-${values[1]}`);
  };

  // UPDATED: handleShort now only updates the sortOrder state for client-side sorting
  const handleShort = (e) => {
    setSortOrder(e.target.value);
  };

  // --- Data Fetching with React Query ---
  const searchParams = new URLSearchParams(location.search);
  const currentSubCategoryId = searchParams.get("subcategory");

  const fetchProducts = async () => {
    const token = localStorage.getItem("token");
    const filterQuery = [];

    if (priceRangeFilter) filterQuery.push(`filter=${priceRangeFilter}`);
    if (currentSubCategoryId)
      filterQuery.push(`subcategory=${currentSubCategoryId}`);

    const finalQuery =
      filterQuery.length > 0 ? `?${filterQuery.join("&")}` : "";
    try {
      const res = await axios.get(`${baseUrl}productlist${finalQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (error) {
      console.error("Product Fetch Error:", error);
      toast.error("Failed to fetch products");
      throw new Error("Failed to fetch products");
    }
  };

  const { data: productsResponse, isLoading } = useQuery({
    queryKey: ["products-list", priceRangeFilter, location.search], // REMOVED dependency on sorting filter
    queryFn: fetchProducts,
    keepPreviousData: true,
  });

  const productsFromAPI = productsResponse?.products || [];
  const totalProducts = productsResponse?.totalCount || productsFromAPI.length;

  // --- Client-Side Sorting Logic ---
  const getFinalPrice = (product) => {
    const discountPrice = parseFloat(product.discount_price);
    // Use discount_price if it's a valid number greater than 0, otherwise use reguler_price
    return discountPrice > 0
      ? discountPrice
      : parseFloat(product.reguler_price);
  };

  const displayedProducts = useMemo(() => {
    let filtered = [...productsFromAPI];

    // Client-side price filtering (trigger only on appliedValues)
    filtered = filtered.filter(p => {
      const price = getFinalPrice(p);
      return price >= appliedValues[0] && price <= appliedValues[1];
    });

    if (sortOrder === "low-to-high") {
      filtered.sort((a, b) => getFinalPrice(a) - getFinalPrice(b));
    } else if (sortOrder === "high-to-low") {
      filtered.sort((a, b) => getFinalPrice(b) - getFinalPrice(a));
    }
    return filtered;
  }, [productsFromAPI, sortOrder, appliedValues]);

  // --- Pagination Logic ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedValues, sortOrder, currentSubCategoryId]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // Use useMemo to prevent recalculation on every render unless changing page/products
  const currentItems = useMemo(() =>
    displayedProducts.slice(indexOfFirstItem, indexOfLastItem),
    [displayedProducts, indexOfFirstItem, indexOfLastItem]
  );
  const totalPages = Math.ceil(displayedProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Price Range Initialization Logic ---
  useEffect(() => {
    if (productsFromAPI.length > 0 && !hasInitializedBounds) {
      const prices = productsFromAPI.map(p => {
        const discountPrice = parseFloat(p.discount_price);
        return discountPrice > 0 ? discountPrice : parseFloat(p.reguler_price);
      });
      const maxPrice = Math.ceil(Math.max(...prices));

      setMinBound(0);
      setMaxBound(maxPrice > 0 ? maxPrice : 1500);
      setValues([0, maxPrice > 0 ? maxPrice : 1500]);
      setAppliedValues([0, maxPrice > 0 ? maxPrice : 1500]);
      setHasInitializedBounds(true);
    }
  }, [productsFromAPI, hasInitializedBounds]);

  // Reset initialization when location changes
  useEffect(() => {
    setHasInitializedBounds(false);
  }, [location.search]);

  // --- Categories and Sub-Category Details Fetching ---
  const { data: categoriesArray = [], isLoading: categoriesLoading } = useQuery(
    {
      queryKey: ["categories-list-on-shop"],
      queryFn: async () => {
        const token = localStorage.getItem("token");
        try {
          const res = await fetch(`${baseUrl}catagorylist`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error("Failed to fetch categories");
          const data = await res.json();
          return data.catagories || data.data || [];
        } catch (e) {
          console.error("Category Fetch Error:", e);
          toast.error("Failed to fetch categories");
          return [];
        }
      },
    }
  );

  const { data: subCategoryDetails } = useQuery({
    queryKey: ["subCategoryDetails", currentSubCategoryId],
    queryFn: async () => {
      if (!currentSubCategoryId) return null;
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(
          `${baseUrl}sub-category/details/${currentSubCategoryId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return res.data.data;
      } catch (error) {
        console.error("Failed to fetch sub-category details", error);
        return null;
      }
    },
    enabled: !!currentSubCategoryId,
  });

  const pageTitle = subCategoryDetails?.name || "Shop";
  const shopTitleBg = "#D93A3A",
    filterPanelBg = "#f0f2f5",
    filterTitleColor = "#D93A3A",
    filterButtonGradient = "linear-gradient(to right, #e5272e, #f59332)",
    primaryButtonTextColor = "#ffffff";

  return (
    <>
      <style>{`
        /* --- Styles for Sidebar Dropdown --- */
        /* --- Shop Title Bar --- */
        .shop-title-bar {
          background: #d93a38;
          padding: 80px 0;
          margin-bottom: 50px;
          text-align: center;
          color: #fff;
          position: relative;
          overflow: hidden;
        }
        .shop-title-bar::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: url("https://www.transparenttextures.com/patterns/carbon-fibre.png");
          opacity: 0.1;
        }
        .shop-title-bar h1 {
          font-size: 3.5rem;
          font-weight: 900;
          margin: 0;
          letter-spacing: -0.04em;
          text-transform: uppercase;
          position: relative;
          z-index: 1;
        }

        /* --- Sidebar Styles --- */
        .filter-sidebar { 
          background-color: #ffffff; 
          padding: 24px; 
          border-radius: 20px; 
          position: sticky; 
          top: 20px; 
          border: 1px solid #f0f0f0;
          box-shadow: 0 10px 30px rgba(0,0,0,0.02);
        }
        .filter-section h2, .filter-section h3 { 
          font-size: 1.1rem; 
          font-weight: 800; 
          color: #1a1a1a; 
          margin-bottom: 20px; 
          letter-spacing: -0.02em;
        }
        .item-category { 
          margin-bottom: 12px; 
          display: flex;
          flex-direction: column; /* Force subcategories to be below */
          width: 100%;
        }
        .category-item-row { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          width: 100%;
        }
        .category-name-link { 
          font-size: 0.95rem; 
          font-weight: 600; 
          color: #444; 
          padding: 8px 0;
          transition: all 0.3s ease;
          flex-grow: 1;
        }
        .category-name-link:hover { color: #d93a38; padding-left: 5px; }
        .toggle-icon { 
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          background: #f8f8f8;
          color: #666;
          font-size: 1.2rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-left: 10px;
        }
        .toggle-icon:hover { background: #d93a38; color: #fff; }
        .sub-category-list { 
          padding-left: 15px; 
          border-left: 2px solid #eee; 
          margin-left: 5px; 
          margin-top: 5px;
          width: 100%;
        }
        .sub-category-link { 
          font-size: 0.85rem; 
          color: #666; 
          padding: 6px 0;
          display: block;
          transition: all 0.3s ease;
        }
        .sub-category-link:hover { color: #d93a38; transform: translateX(5px); }
        
        /* --- Enhanced Range Slider --- */
        .range-track-container {
          padding: 10px 10px 30px; 
          width: 100%;
        }
        .range-track { 
          height: 4px; 
          width: 100%; 
          border-radius: 4px; 
          background: #e2e8f0;
          position: relative; 
          align-self: center;
        }
        .range-thumb { 
          height: 18px; 
          width: 18px; 
          background-color: #1a2a47; /* Dark Navy/Slate from reference */
          border: none;
          border-radius: 50%; 
          box-shadow: 0 2px 5px rgba(0,0,0,0.2); 
          cursor: pointer;
          transition: transform 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 40;
          outline: none !important;
        }
        .range-thumb:focus { outline: none; transform: scale(1.1); }
        .price-range-label {
          font-size: 0.9rem;
          font-weight: 800;
          color: #d93a38;
          text-transform: uppercase;
          margin-bottom: 8px;
          letter-spacing: 0.5px;
        }
        .price-range-value-text {
          font-size: 0.85rem;
          color: #666;
          margin-bottom: 20px;
        }
        .filter-apply-btn { 
          background: #f16522 !important; /* Orange-Red from reference */
          border: none; 
          color: #fff !important; 
          font-weight: 600; 
          font-size: 0.95rem; 
          padding: 10px; 
          width: 100%; 
          border-radius: 8px;
          transition: all 0.3s ease;
          letter-spacing: 0.5px;
        }
        .filter-apply-btn:hover { background: #d93a38 !important; transform: translateY(-1px); }
        
        .shop-toolbar { 
          background: #fff;
          padding: 15px 20px;
          border-radius: 15px;
          border: 1px solid #f0f0f0;
          margin-bottom: 30px; 
        }
        .shop-sort-select {
          border-radius: 10px;
          border: 1px solid #eee;
          padding: 8px 15px;
          font-weight: 600;
        }
        .shop-sort-select:focus {
          border-color: #d93a38;
          box-shadow: 0 0 0 0.2rem rgba(217, 58, 56, 0.1);
        }

        @media (max-width: 768px) {
          .shop-title-bar { padding: 40px 0; margin-bottom: 30px; }
          .shop-title-bar h1 { font-size: 2.2rem; }
          .filter-sidebar { padding: 15px; border-radius: 12px; }
          .shop-toolbar {
            display: flex !important;
            justify-content: space-between;
            align-items: center;
            position: relative;
            padding-bottom: 50px;
          }
          .shop-toolbar .Showing {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            text-align: center;
            width: 100%;
          }
          .filter-toggle-btn {
        background: #d93a38 !important;
        color: white !important;
        border: none;
        padding: 8px 16px;
        border-radius: 8px;
        font-weight: 600;
    }
          .shop-toolbar .filter-toggle-btn {
            order: 1;
          }
          .shop-toolbar .main-Default-sorting {
            order: 3;
          }
        }
      `}</style>

      <div className="shop-page-main-container">
        <section className="shop-title-bar">
          <div className="container">
            <h1>{pageTitle.toUpperCase()}</h1>
          </div>
        </section>

        <section className="mainsec-5 py-4 container">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-3 col-md-4 filter-sidebar-wrapper">
                {/* <button
                  className="btn filter-toggle-btn d-md-none"
                  type="button"
                  onClick={() => setIsFilterVisible(!isFilterVisible)}
                >
                  <span>Filters</span>
                  <span>{isFilterVisible ? "−" : "+"}</span>
                </button> */}

                <div
                  className={`filter-collapse-container collapse d-md-block ${isFilterVisible ? "show" : ""
                    }`}
                >
                  <aside className="filter-sidebar">
                    <div className="filter-section">
                      <h2>Categories</h2>
                      <div className="filter_category_product">
                        {categoriesLoading ? (
                          <small className="text-muted d-block">Loading...</small>
                        ) : categoriesArray?.map((category) => (
                          <div key={category._id} className="item-category">
                            <div className="category-item-row">
                              <Link
                                to={`/category/${category._id}`}
                                className="category-name-link text-decoration-none"
                              >
                                {category?.catagoryname}
                              </Link>
                              <div
                                className="toggle-icon"
                                onClick={(e) => handleIconToggle(e, category._id)}
                              >
                                {activeCategory === category._id ? "−" : "+"}
                              </div>
                            </div>
                            <ul
                              className={`sub-category-list list-unstyled transition-all ${activeCategory === category._id ? "d-block" : "d-none"
                                }`}
                            >
                              {loadingSubCat === category._id ? (
                                <li><small className="text-muted">Loading...</small></li>
                              ) : subCategories[category._id]?.map((sub) => (
                                <li key={sub._id}>
                                  <Link
                                    to={`/shop?subcategory=${sub._id}`}
                                    className="sub-category-link text-decoration-none"
                                  >
                                    {sub.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="filter-section mt-5">
                      <div className="price-range-label">PRICE</div>
                      <div className="price-range-value-text">
                        Price Range: £{values[0]} — £{values[1]}
                      </div>
                      <div className="content-filter-price">
                        <div className="range-track-container">
                          <Range
                            values={values}
                            step={STEP}
                            min={minBound}
                            max={maxBound}
                            onChange={(vals) => setValues(vals)}
                            renderTrack={({ props, children }) => {
                              const { onMouseDown, onTouchStart, style, ref } = props;
                              return (
                                <div
                                  onMouseDown={onMouseDown}
                                  onTouchStart={onTouchStart}
                                  style={{
                                    ...style,
                                    height: "36px",
                                    display: "flex",
                                    width: "100%",
                                  }}
                                >
                                  <div
                                    ref={ref}
                                    className="range-track"
                                    style={{
                                      background: `linear-gradient(to right, #e2e8f0 ${((values[0] - minBound) / (maxBound - minBound || 1)) * 100}%, #1a2a47 ${((values[0] - minBound) / (maxBound - minBound || 1)) * 100}%, #1a2a47 ${((values[1] - minBound) / (maxBound - minBound || 1)) * 100}%, #e2e8f0 ${((values[1] - minBound) / (maxBound - minBound || 1)) * 100}%)`,
                                    }}
                                  >
                                    {children}
                                  </div>
                                </div>
                              );
                            }}
                            renderThumb={({ props, isDragged }) => {
                              const { key, ...restProps } = props;
                              return (
                                <div
                                  {...restProps}
                                  key={key}
                                  className="range-thumb"
                                  style={{
                                    ...restProps.style,
                                  }}
                                />
                              );
                            }}
                          />
                        </div>
                        <button
                          onClick={handleFilterByPriceRange}
                          type="button"
                          className="btn filter-apply-btn"
                        >
                          Filter
                        </button>
                      </div>
                    </div>
                  </aside>
                </div>
              </div>

              <div className="col-lg-9 col-md-8 position-relative">
                {/* Shop Closed Overlay */}
                {!isShopOpen && (
                  <div
                    className="shop-closed-overlay"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(4px)',
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                    }}
                  >
                    <div
                      className="shop-closed-message p-4 text-center"
                      style={{
                        background: '#fff',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        border: '2px solid #d93a38',
                        maxWidth: '80%'
                      }}
                    >
                      <h3 style={{ color: '#d93a38', fontWeight: '800', margin: 0 }}>
                        SHOP IS CURRENTLY CLOSED
                      </h3>
                      <p className="mb-0 mt-2 text-muted">
                        We are not accepting orders at the moment. Please check back later.
                      </p>
                    </div>
                  </div>
                )}

                <div style={{ filter: !isShopOpen ? 'blur(5px)' : 'none', pointerEvents: !isShopOpen ? 'none' : 'auto' }}>
                  <div className="shop-toolbar d-md-flex align-items-center justify-content-md-between mb-4">
                    <button
                      className="btn filter-toggle-btn d-md-none"
                      type="button"
                      onClick={() => setIsFilterVisible(!isFilterVisible)}
                    >
                      <span>Filters</span>
                      <span>{isFilterVisible ? "−" : "+"}</span>
                    </button>

                    <div className="Showing mb-2 mb-md-0">
                      Showing {displayedProducts.length} of {totalProducts}{" "}
                      results
                    </div>

                    <div className="main-Default-sorting">
                      <select
                        style={{ minWidth: "200px" }}
                        className="form-select shop-sort-select"
                        value={sortOrder}
                        onChange={handleShort}
                      >
                        <option value="">Default sorting</option>
                        <option value="low-to-high">Price : Low to High</option>
                        <option value="high-to-low">Price : High to Low</option>
                      </select>
                    </div>
                  </div>
                  {isLoading ? (
                    <div className="text-center py-5">
                      <Loder />
                    </div>
                  ) : (
                    <>
                      <div className="row g-3 product-grid-row">
                        {currentItems.length > 0 ? (
                          currentItems.map((item) => (
                            <div
                              className="col-6 col-md-4 product-item-col mb-4"
                              key={item._id}
                            >
                              <ProductCard item={item} categories={categories} />
                            </div>
                          ))
                        ) : (
                          <div className="col-12 text-center py-5">
                            <p className="no-products-message">
                              No products found.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="d-flex justify-content-center justify-content-md-end mt-4">
                          <nav aria-label="Page navigation">
                            <ul className="pagination">
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                  style={{ color: '#d93a38', borderColor: '#dee2e6' }}
                                >
                                  &laquo;
                                </button>
                              </li>

                              {(() => {
                                const pages = [];
                                const addPage = (p) => pages.push(
                                  <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(p)} style={currentPage === p ? { backgroundColor: '#d93a38', borderColor: '#d93a38', color: '#fff' } : { color: '#d93a38', borderColor: '#dee2e6' }}>{p}</button>
                                  </li>
                                );
                                const addDots = (key) => pages.push(
                                  <li key={key} className="page-item disabled"><span className="page-link" style={{ color: '#d93a38', borderColor: '#dee2e6' }}>...</span></li>
                                );

                                if (totalPages <= 7) {
                                  for (let i = 1; i <= totalPages; i++) addPage(i);
                                } else {
                                  addPage(1);
                                  if (currentPage > 3) addDots('d1');

                                  let start = Math.max(2, currentPage - 1);
                                  let end = Math.min(totalPages - 1, currentPage + 1);

                                  if (currentPage <= 3) {
                                    end = 5;
                                    start = 2;
                                  }
                                  if (currentPage >= totalPages - 2) {
                                    start = totalPages - 4;
                                    end = totalPages - 1;
                                  }

                                  for (let i = start; i <= end; i++) {
                                    if (i > 1 && i < totalPages) addPage(i);
                                  }

                                  if (currentPage < totalPages - 2) addDots('d2');
                                  addPage(totalPages);
                                }
                                return pages;
                              })()}

                              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                  style={{ color: '#d93a38', borderColor: '#dee2e6' }}
                                >
                                  &raquo;
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <DeliveryInfo />
    </>
  );
};

export default Shop;
